import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AbandonedCartService from "services/AbandonedCartService";

const initialState = {
  loading: false,
  carts: [],
  total: 0,
  stats: {
    total: 0,
    active: 0,
    recovered: 0,
    potential_revenue: 0,
    recovered_revenue: 0,
  },
  message: "",
  showMessage: false,
  filters: {
    status: null,
    search: null,
  },
  settings: {
    enabled: true,
    coupon_enabled: false,
    coupon_percent: 10,
    coupon_valid_days: 7,
    reminder_hours: "1,24,72",
    max_age_days: 30,
  },
  settingsLoading: false,
  settingsSaving: false,
};

export const getAbandonedCarts = createAsyncThunk(
  "abandonedCarts/getAbandonedCarts",
  async ({ page = 1, pagelimit = 20, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await AbandonedCartService.getAbandonedCarts(
        page,
        pagelimit,
        filters,
      );
      return {
        carts: response.data || [],
        total: response.total || 0,
        stats: response.stats || initialState.stats,
        filters,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching abandoned carts");
    }
  },
);

export const getAbandonedCartSettings = createAsyncThunk(
  "abandonedCarts/getSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AbandonedCartService.getSettings();
      return response.data || {};
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching settings");
    }
  },
);

export const updateAbandonedCartSettings = createAsyncThunk(
  "abandonedCarts/updateSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AbandonedCartService.updateSettings(data);
      return response.data || data;
    } catch (err) {
      return rejectWithValue(err.message || "Error saving settings");
    }
  },
);

const abandonedCartSlice = createSlice({
  name: "abandonedCarts",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAbandonedCarts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAbandonedCarts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.carts = payload.carts;
        state.total = payload.total;
        state.stats = payload.stats || state.stats;
        state.filters = payload.filters || state.filters;
        state.showMessage = false;
      })
      .addCase(getAbandonedCarts.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getAbandonedCartSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(getAbandonedCartSettings.fulfilled, (state, { payload }) => {
        state.settingsLoading = false;
        state.settings = { ...state.settings, ...payload };
      })
      .addCase(getAbandonedCartSettings.rejected, (state, { payload }) => {
        state.settingsLoading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(updateAbandonedCartSettings.pending, (state) => {
        state.settingsSaving = true;
      })
      .addCase(updateAbandonedCartSettings.fulfilled, (state, { payload }) => {
        state.settingsSaving = false;
        state.settings = { ...state.settings, ...payload };
      })
      .addCase(updateAbandonedCartSettings.rejected, (state, { payload }) => {
        state.settingsSaving = false;
        state.message = payload;
        state.showMessage = true;
      });
  },
});

export const { clearMessage } = abandonedCartSlice.actions;
export default abandonedCartSlice.reducer;
